import * as React from 'react';

import {
    EmptyBox,
    HeaderListSkeleton,
    LazyBreadcrumb,
    ListSkeleton,
    SkeletonLoader,
} from '@europrocurement/flexy-components';
import {
    FactureOpenSearchPrescriber,
    FactureOpenSearchPrescriberSelectorMain,
    FACTURES_OPEN_SEARCH_PRESCRIBER_SLICE_NAME,
    useFacturationService,
} from '@europrocurement/l2d-domain';
import { DataSource } from '@europrocurement/l2d-redux-utils';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useDispatch, useSelector } from 'react-redux';
import {
    FacturesTable,
    PossibleTypes,
    useBilling,
    useDocumentPreviewContext,
    useSearchBar,
} from '@europrocurement/l2d-modules';
import { AnyAction } from '@reduxjs/toolkit';
import { Box, CircularProgress } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useInitialLoadingState, usePrescriber, useFactureTable } from '@europrocurement/l2d-hooks';
import { type AppDispatch } from '../../../../../redux';

interface InvoicesProps {
    prescriberId?: number;
}

/**
 * Liste des factures liées à un prescripteur.
 * Utilise un slice Redux dédié pour éviter les conflits de filtres avec la liste générale.
 */
const Invoices: React.FunctionComponent<InvoicesProps> = function ({
    prescriberId: prescriberIdFromParent,
}) {
    const { prescriberId } = usePrescriber({ prescriberIdFromParent });
    const prescriberIdRef = React.useRef<number | undefined>();
    const [filtersSet, setFiltersSet] = React.useState(false);

    const datasource: DataSource<FactureOpenSearchPrescriber> = useSelector(
        FactureOpenSearchPrescriberSelectorMain,
    );
    const { isInitialLoading, isInError } = useInitialLoadingState(datasource);
    const { setIsSearchBarPath, setSearchBarType } = useSearchBar();

    const dispatch = useDispatch<AppDispatch>();
    const { enqueueSnackbar } = useSnackbar();

    const ddmIdFilter = 3;
    const { downloadInvoice } = useBilling({});

    const { previewLoading, setPreviewLoading, setDocumentPreview } = useDocumentPreviewContext();

    const { columns, filters, rowActions, preFilters } = useFactureTable({
        prescriberIdRef,
        ddmIdFilter,
    });

    const { getFactureOpenSearchPrescriber } = useFacturationService();
    const getFactureOpenSearchPrescriberRef = React.useRef(getFactureOpenSearchPrescriber);
    getFactureOpenSearchPrescriberRef.current = getFactureOpenSearchPrescriber;

    const getInvoiceLabel = (item: FactureOpenSearchPrescriber): string => {
        if (item.type && item.type === 'AVOIR') {
            return `Avoir ${item.factureNumero}.pdf`;
        }
        return `Facture ${item.factureNumero}.pdf`;
    };

    const handleRowClick = async (item: FactureOpenSearchPrescriber) => {
        if (previewLoading) {
            return;
        }

        if (item.id) {
            setPreviewLoading(true);
            try {
                const blob = await downloadInvoice(item.id as number);
                setDocumentPreview({
                    fileName: getInvoiceLabel(item),
                    fileType: 'pdf',
                    content: blob as unknown as Blob,
                });
            } catch (err) {
                console.error(err);
                enqueueSnackbar(`Une erreur est survenue lors de la récupération de la facture`, {
                    variant: 'error',
                });
            }
            setPreviewLoading(false);
        }
    };

    const breadcrumbItems = [
        {
            link: '/accueil',
            icon: {
                icon: ['fasl', 'house'] as IconProp,
                sx: { display: 'flex', alignItems: 'center' },
            },
        },
        {
            link: '/formalites/factures',
            path: 'Mes factures',
        },
        'Liste des factures du prescripteur',
    ];

    React.useEffect(() => {
        setSearchBarType(PossibleTypes.FormalityInvoicePrescriber);
        setIsSearchBarPath(true);
        return () => {
            setSearchBarType(undefined);
            setIsSearchBarPath(false);
        };
    }, [setIsSearchBarPath, setSearchBarType]);

    React.useEffect(() => {
        if (prescriberId && prescriberId !== prescriberIdRef.current) {
            dispatch({
                type: `${FACTURES_OPEN_SEARCH_PRESCRIBER_SLICE_NAME}/setmainFilter`,
                payload: {
                    key: 'ddmId',
                    value: ddmIdFilter,
                },
            });
            dispatch({
                type: `${FACTURES_OPEN_SEARCH_PRESCRIBER_SLICE_NAME}/setmainFilter`,
                payload: {
                    key: 'prescriberId',
                    value: prescriberId,
                },
            });
            dispatch({
                type: `${FACTURES_OPEN_SEARCH_PRESCRIBER_SLICE_NAME}/setmainFilter`,
                payload: {
                    key: 'enPreparation',
                    value: false,
                },
            });
            prescriberIdRef.current = prescriberId;
            setFiltersSet(true);
        }
    }, [prescriberId, dispatch]);

    React.useEffect(() => {
        if (filtersSet) {
            dispatch(getFactureOpenSearchPrescriberRef.current({}) as unknown as AnyAction);
        }
    }, [dispatch, filtersSet]);

    const errorContent = React.useMemo(
        () => (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100vh',
                    width: '100%',
                }}
            >
                <EmptyBox
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'space-evenly',
                        height: '25%',
                        width: '50%',
                    }}
                    text="Une erreur s'est produite lors de la récupération des factures."
                    subText="Veuillez recharger la page. Si le problème persiste, contactez votre administrateur."
                />
            </Box>
        ),
        [],
    );

    return (
        <SkeletonLoader
            isLoading={isInitialLoading}
            skeleton={
                <>
                    <HeaderListSkeleton />
                    <ListSkeleton columns={6} />
                </>
            }
            isInError={isInError}
            errorChildren={errorContent}
        >
            <Box sx={{ position: 'relative' }}>
                <LazyBreadcrumb
                    path={breadcrumbItems}
                    separator=" / "
                    sx={{ marginBottom: '1.5em' }}
                />

                {previewLoading && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            zIndex: 1,
                        }}
                    >
                        <CircularProgress />
                    </Box>
                )}

                {prescriberIdRef.current && (
                    <FacturesTable
                        datasource={datasource}
                        preFilters={preFilters}
                        columns={columns}
                        filters={filters}
                        fetchData={getFactureOpenSearchPrescriberRef.current}
                        rowActions={rowActions}
                        onRowClick={handleRowClick}
                    />
                )}
            </Box>
        </SkeletonLoader>
    );
};

export default Invoices;
